/**
 * Reports suite data seam (#1115; slice `web-shell`; docs/reports/design.md §1). Every read goes through the generated
 * SDK + `unwrap()`, so a failed lookup throws and is never rendered as an empty list or a zero (hard rule 10).
 *
 * The URL owns the filter bar and the open drill (`ReportsSearch`), so every view and every drill is a link.
 */
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { unwrap } from '../../lib/api/client'
import {
  createViewApiV1ReportsSuiteViewsPost,
  type Drill,
  type DrillPage,
  deleteViewApiV1ReportsSuiteViewsViewIdDelete,
  getCatalogueApiV1ReportsSuiteGet,
  getDrillApiV1ReportsSuiteKeyDrillGet,
  getLicencesApiV1ReportsSuiteLicencesGet,
  getReportApiV1ReportsSuiteKeyGet,
  type LicenceMarginReport,
  listViewsApiV1ReportsSuiteViewsGet,
  patchViewApiV1ReportsSuiteViewsViewIdPatch,
  type ReportCatalogue,
  type ReportFilters,
  type ReportResult,
  type SavedViewIn,
  type SavedViewOut,
  type SavedViewPatch,
} from '../../lib/api/generated'

export const NATIVE_REPORTS = [
  'overview',
  'volume',
  'backlog',
  'responsiveness',
  'resolution',
  'sla',
  'customers',
  'team',
  'satisfaction',
  'onsite',
  'contractors',
  'time',
  'billing',
  'riley',
  'project_portfolio',
  'project_ageing',
  'project_forecast',
  'project_hardware',
  'project_margin',
  'project_workload',
] as const
export type NativeReportKey = (typeof NATIVE_REPORTS)[number]
export type ReportKey = SavedViewIn['report']

export const isNativeReport = (key: string): key is NativeReportKey => (NATIVE_REPORTS as readonly string[]).includes(key)

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export const isUuid = (value: string): boolean => UUID.test(value)

/** What `/reports/$reportId` renders for an id. */
export type ReportRouteKind =
  | { kind: 'scheduled'; id: string }
  | { kind: 'native'; key: NativeReportKey }
  | { kind: 'licences' }
  | { kind: 'not_found' }

export function reportRouteKind(id: string): ReportRouteKind {
  if (isUuid(id)) return { kind: 'scheduled', id }
  if (id === 'licences') return { kind: 'licences' }
  if (isNativeReport(id)) return { kind: 'native', key: id }
  return { kind: 'not_found' }
}

// --- URL search --------------------------------------------------------------------------------------------------------

export const RANGES = ['7d', '4w', '12w', '6m', '12m', 'custom'] as const
export type RangeKey = (typeof RANGES)[number]
export const GRAINS = ['day', 'week', 'month'] as const
export type Grain = (typeof GRAINS)[number]

export const DIMENSIONS = ['owners', 'customers', 'priorities', 'channels', 'categories', 'contract_kinds'] as const
export type Dimension = (typeof DIMENSIONS)[number]

/**
 * The URL search state. Defaults are omitted, so a plain `/reports/volume` is "the API's default window for the grain,
 * weekly, vs prior period, everything". `range` is a rolling preset (recomputed from today on every load); `custom`
 * uses `from`/`to`. `drill` + `dp` (+ `dr` when the drill names another report) open the drill drawer.
 */
export interface ReportsSearch {
  /** Global brand scope from the shell's useBrandScope/BrandSwitcher; absent and `all` both mean all accessible brands. */
  brand?: string
  range?: RangeKey
  /** YYYY-MM-DD, with range=custom. */
  from?: string
  to?: string
  grain?: Exclude<Grain, 'week'>
  compare?: 'none'
  owners?: string[]
  customers?: string[]
  priorities?: string[]
  channels?: string[]
  categories?: string[]
  contract_kinds?: string[]
  /** Open drill: the metric key. */
  drill?: string
  /** Open drill's params, as `k:v` pairs. */
  dp?: string[]
  /** Open drill's report when it is not the page's report. */
  dr?: NativeReportKey
  /** Licence margin's tab (#1142); absent = Licences. */
  tab?: LicenceTab
}

export const LICENCE_TABS = ['licences', 'usage', 'renewals'] as const
export type LicenceTab = (typeof LICENCE_TABS)[number]

const DAY = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : undefined
}

function day(value: unknown): string | undefined {
  return typeof value === 'string' && DAY.test(value) ? value : undefined
}

/** A list param: TanStack gives an array for `?a=1&a=2` (JSON search) and a string for a hand-typed `?a=1`. */
function list(value: unknown, max = 50): string[] | undefined {
  const raw = Array.isArray(value) ? value : typeof value === 'string' ? [value] : []
  const out = [...new Set(raw.filter((v): v is string => typeof v === 'string').map((v) => v.trim().slice(0, 120)))].filter(Boolean)
  return out.length ? out.slice(0, max) : undefined
}

/** validateSearch for `/reports/$reportId` and `/reports`: anything unknown or malformed is dropped, never guessed. */
export function parseReportsSearch(raw: Record<string, unknown>): ReportsSearch {
  const out: ReportsSearch = {
    brand: typeof raw.brand === 'string' && /^(all|[a-z][a-z0-9_]{1,40})$/.test(raw.brand) ? raw.brand : undefined,
    range: oneOf(raw.range, RANGES),
    from: day(raw.from),
    to: day(raw.to),
    grain: oneOf(raw.grain, ['day', 'month'] as const),
    compare: raw.compare === 'none' ? 'none' : undefined,
    // Owner ids are UUIDs to the API; a malformed one would 422 the whole report, so it is dropped here.
    owners: list(raw.owners)?.filter(isUuid),
    customers: list(raw.customers),
    priorities: list(raw.priorities),
    channels: list(raw.channels),
    categories: list(raw.categories),
    contract_kinds: list(raw.contract_kinds),
    drill: typeof raw.drill === 'string' && /^[\w.:-]{1,80}$/.test(raw.drill) ? raw.drill : undefined,
    dp: list(raw.dp, 12)?.filter((p) => p.includes(':')),
    dr: typeof raw.dr === 'string' && isNativeReport(raw.dr) ? raw.dr : undefined,
    tab: oneOf(raw.tab, ['usage', 'renewals'] as const),
  }
  // A shared link with dates but no range is a custom window.
  if (!out.range && (out.from || out.to)) out.range = 'custom'
  if (out.range !== 'custom') {
    out.from = undefined
    out.to = undefined
  }
  if (!out.drill) {
    out.dp = undefined
    out.dr = undefined
  }
  if (out.dp && out.dp.length === 0) out.dp = undefined
  if (out.owners && out.owners.length === 0) out.owners = undefined
  for (const key of Object.keys(out) as (keyof ReportsSearch)[]) if (out[key] === undefined) delete out[key]
  return out
}

export function isoDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

/** The first day of a rolling preset ending today (inclusive). */
export function rangeStart(range: Exclude<RangeKey, 'custom'>, now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (range === '6m' || range === '12m') {
    d.setMonth(d.getMonth() - (range === '6m' ? 6 : 12))
    d.setDate(d.getDate() + 1)
  } else {
    d.setDate(d.getDate() - ({ '7d': 7, '4w': 28, '12w': 84 } as const)[range] + 1)
  }
  return isoDay(d)
}

/** The API's ReportFilters for a search (flat query params). The API defaults the window when `from` is absent. */
export function toApiFilters(search: ReportsSearch, _now: Date = new Date()): ReportFilters {
  const f: ReportFilters = {}
  if (search.brand) f.brand = search.brand
  if (search.range === 'custom') {
    if (search.from) f.from = search.from
    if (search.to) f.to = search.to
  } else if (search.range) {
    // The API resolves a rolling preset against its own today, so a saved view rolls forward.
    f.range = search.range
  }
  if (search.grain) f.grain = search.grain
  if (search.compare) f.compare = search.compare
  for (const dim of DIMENSIONS) {
    const values = search[dim]
    if (values?.length) f[dim] = values
  }
  return f
}

/** A saved view's filters as a search: a rolling preset stays rolling; bare dates become a custom window. */
export function searchFromFilters(filters: ReportFilters): ReportsSearch {
  const range = filters.range ?? (filters.from || filters.to ? 'custom' : undefined)
  return parseReportsSearch({ ...filters, range })
}

/** A filters-only patch that clears everything the filter bar sets. */
export const CLEARED_FILTERS: Partial<ReportsSearch> = {
  range: undefined,
  from: undefined,
  to: undefined,
  grain: undefined,
  compare: undefined,
  owners: undefined,
  customers: undefined,
  priorities: undefined,
  channels: undefined,
  categories: undefined,
  contract_kinds: undefined,
}

/** The search patch that opens a drill (and the inverse, `CLOSED_DRILL`). */
export function drillSearch(drill: Drill, pageReport: string | undefined): Partial<ReportsSearch> {
  const dp = Object.entries(drill.params ?? {}).map(([k, v]) => `${k}:${v}`)
  return { drill: drill.metric, dp: dp.length ? dp : undefined, dr: drill.report !== pageReport ? drill.report : undefined }
}

export const CLOSED_DRILL: Partial<ReportsSearch> = { drill: undefined, dp: undefined, dr: undefined }

// --- queries -----------------------------------------------------------------------------------------------------------

const STALE = 60_000

export const catalogueQuery = (brand?: string) =>
  queryOptions({
    queryKey: ['reports-suite', 'catalogue', brand ?? 'all'],
    queryFn: ({ signal }): Promise<ReportCatalogue> => unwrap(getCatalogueApiV1ReportsSuiteGet({ query: brand ? { brand } : {}, signal })),
    staleTime: STALE,
  })

export const suiteReportQuery = (key: NativeReportKey, filters: ReportFilters) =>
  queryOptions({
    queryKey: ['reports-suite', 'report', key, filters],
    queryFn: ({ signal }): Promise<ReportResult> => unwrap(getReportApiV1ReportsSuiteKeyGet({ path: { key }, query: filters, signal })),
    staleTime: STALE,
  })

export const DRILL_PAGE = 50

export const drillQuery = (key: NativeReportKey, metric: string, params: string[], filters: ReportFilters) =>
  infiniteQueryOptions({
    queryKey: ['reports-suite', 'drill', key, metric, params, filters],
    initialPageParam: null as string | null,
    queryFn: ({ signal, pageParam }): Promise<DrillPage> =>
      unwrap(
        getDrillApiV1ReportsSuiteKeyDrillGet({
          path: { key },
          query: { ...filters, metric, params, limit: DRILL_PAGE, ...(pageParam ? { cursor: pageParam } : {}) },
          signal,
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: STALE,
  })

export const viewsQuery = (brand?: string) =>
  queryOptions({
    queryKey: ['reports-suite', 'views', brand ?? 'all'],
    queryFn: ({ signal }): Promise<SavedViewOut[]> => unwrap(listViewsApiV1ReportsSuiteViewsGet({ query: brand ? { brand } : {}, signal })),
    staleTime: STALE,
  })

export const licencesQuery = (brand?: string) =>
  queryOptions({
    queryKey: ['reports-suite', 'licences', brand ?? 'all'],
    queryFn: ({ signal }): Promise<LicenceMarginReport> =>
      unwrap(getLicencesApiV1ReportsSuiteLicencesGet({ query: brand ? { brand } : {}, signal })),
    staleTime: STALE,
  })

// --- writes (my own views; the API enforces ownership) -----------------------------------------------------------------

export function createView(body: SavedViewIn): Promise<SavedViewOut> {
  return unwrap(createViewApiV1ReportsSuiteViewsPost({ body }))
}

export function patchView(id: string, body: SavedViewPatch): Promise<SavedViewOut> {
  return unwrap(patchViewApiV1ReportsSuiteViewsViewIdPatch({ path: { view_id: id }, body }))
}

export function deleteView(id: string): Promise<unknown> {
  return unwrap(deleteViewApiV1ReportsSuiteViewsViewIdDelete({ path: { view_id: id } }))
}
